import { orthoTypeToTranslationKeyMap } from '@/src/app/features/ortho/utils/ortho.utils';
import { DictionaryTranslation } from '@/src/app/features/patient/models/patient.model';
import { BaseReportTableComponent } from '@/src/app/features/reports/components/base-report-table/base-report-table.component';
import { OrthodonticTreatmentCompactReportRecord } from '@/src/app/features/reports/features/orthodontic-treatments-compact-report/orthodontic-treatments-compact-report.model';
import { OrthodonticInterventions } from '@/src/app/features/reports/features/orthodontic-treatments-report/orthodontic-treatments-report.model';
import { requiredPlaceholderImage } from '@/src/app/utils/media.utils';
import { DatePipe } from '@angular/common';
import { Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { ReportFiltersModel, ReportType } from '@src/app/features/reports/components/report-filters/models/report-filters.model';
import { CompactPatientHeaderComponent } from '../../../../components/compact-patient-header/compact-patient-header.component';
import { ValueSeparatorComponent } from '@shared/modules/general-commons/components/value-separator/value-separator.component';
import { TreatmentStatusIndicatorComponent } from '@shared/components/treatment-status-indicator/treatment-status-indicator.component';
import { CompactPatientDataComponent } from '../../../../components/compact-patient-data/compact-patient-data.component';
import { InlineReadonlyFieldComponent } from '@shared/modules/readonly-commons/components/inline-readonly-field/inline-readonly-field.component';
import {
  ReadonlyViewColumnsComponent,
  ReadonlyViewColumnDirective
} from '@shared/modules/readonly-commons/components/readonly-view-columns/readonly-view-columns.component';
import { ReadonlyViewSectionHeaderComponent } from '@shared/modules/readonly-commons/components/readonly-view-section-header/readonly-view-section-header.component';
import { ReadonlyFieldComponent } from '@shared/modules/readonly-commons/components/readonly-field/readonly-field.component';
import { OrthoCompactReportImagePairComponent } from './components/ortho-compact-report-image-pair/ortho-compact-report-image-pair.component';
import { ImageThumbnailComponent } from '@shared/components/image-thumbnail/image-thumbnail.component';
import { EmptyTableStateComponent } from '@shared/components/empty-table-state/empty-table-state.component';
import { AgeYearMonthPipe } from '@shared/modules/readonly-commons/pipes/age-year-month/age-year-month.pipe';
import { IfEmptyPipe } from '@shared/pipes/if-empty.pipe';
import { LocalNamePipe } from '@shared/pipes/local-name.pipe';

@Component({
  selector: 'stx-orthodontic-treatment-compact-report-table',
  templateUrl: './orthodontic-treatments-compact-report-table.component.html',
  styleUrls: ['./orthodontic-treatments-compact-report-table.component.scss'],
  imports: [
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    CompactPatientHeaderComponent,
    ValueSeparatorComponent,
    TreatmentStatusIndicatorComponent,
    CompactPatientDataComponent,
    InlineReadonlyFieldComponent,
    MatDivider,
    ReadonlyViewColumnsComponent,
    ReadonlyViewColumnDirective,
    ReadonlyViewSectionHeaderComponent,
    ReadonlyFieldComponent,
    OrthoCompactReportImagePairComponent,
    ImageThumbnailComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    EmptyTableStateComponent,
    TranslateModule,
    DatePipe,
    AgeYearMonthPipe,
    IfEmptyPipe,
    LocalNamePipe
  ]
})
export class OrthodonticTreatmentsCompactReportTableComponent
  extends BaseReportTableComponent<OrthodonticTreatmentCompactReportRecord>
  implements OnInit, OnDestroy
{
  readonly reportType: ReportType = 'ORTHODONTIC_TREATMENTS_COMPACT';
  readonly orthoTypeToTranslationKeyMap = orthoTypeToTranslationKeyMap;
  @Input() reportConfig: ReportFiltersModel;
  @Input() deIdentified = false;
  @Input() showPagination: boolean;
  @Input() readonly showQaOnly: boolean;

  displayedColumns: string[] = ['patient'];
  interventionTranslationsPathMap: Record<OrthodonticInterventions, string> = {
    [OrthodonticInterventions.PresurgicalOrthopedicsType_0]: 'presurgical.tap_lip',
    [OrthodonticInterventions.PresurgicalOrthopedicsType_1]: 'presurgical.tap_stent',
    [OrthodonticInterventions.PresurgicalOrthopedicsType_2]: 'presurgical.max_appliance',
    [OrthodonticInterventions.PresurgicalOrthopedicsType_3]: 'presurgical.nam',
    [OrthodonticInterventions.MixedDentitionType_0]: 'mixed_dentition.fixed_app',
    [OrthodonticInterventions.MixedDentitionType_1]: 'mixed_dentition.max_exp',
    [OrthodonticInterventions.MixedDentitionType_2]: 'mixed_dentition.face_mask',
    [OrthodonticInterventions.PermanentDentitionType_0]: 'permanent_dentition.full_fixed_app',
    [OrthodonticInterventions.PermanentDentitionType_1]: 'permanent_dentition.splints',
    [OrthodonticInterventions.DentitionTypeOther]: 'other'
  };
  readonly placeholderImage = requiredPlaceholderImage;
  readonly dateFormat = 'longDate';

  constructor() {
    super();
  }

  ngOnInit(): void {
    super.onInit();
  }

  ngOnDestroy(): void {
    super.onDestroy();
  }

  getInterventionsDict(interventions: OrthodonticInterventions[]) {
    return interventions
      ?.map(intervention => {
        return { value: intervention, label: this.interventionTranslationsPathMap[intervention] } as DictionaryTranslation;
      })
      .filter(dictTranslation => !!dictTranslation);
  }

  getRankText(rankNumber: string): string {
    return rankNumber == '9' ? 'reports.filters.cannot_access_photo' : rankNumber;
  }
}

@NgModule({
  imports: [OrthodonticTreatmentsCompactReportTableComponent],
  exports: [OrthodonticTreatmentsCompactReportTableComponent]
})
export class OrthodonticTreatmentCompactReportTableComponentModule {}
